import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteModal";
import { useContent } from "@/lib/content";

export default function SolutionDetail() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const { SITE, SOLUTIONS } = useContent();
  const solution = SOLUTIONS.find((s) => s.slug === slug);
  if (!solution) return <Navigate to="/solutions" replace />;
  const others = SOLUTIONS.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${solution.title} in Chennai | DHIYAA SOLAR ENERGY`}
        description={`${solution.tagline}. ${solution.intro.slice(0, 140)}`}
      />
      <PageHero eyebrow={solution.name} title={solution.title} subtitle={solution.tagline} image={solution.image} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="solution-detail">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-lg leading-relaxed text-slate-700">{solution.intro}</p>
            </Reveal>
            <Reveal delay={0.1} className="mt-10">
              <h2 className="font-heading text-2xl font-bold text-brand-navy">
                {solution.slug === "residential-solar" ? "Complete Solution Includes" : solution.slug === "on-grid-solar" ? "Key Components" : "What's Included"}
              </h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {solution.includes.map((item) => (
                  <div key={item} className="flex items-start gap-2.5 rounded-xl border border-slate-100 bg-white p-3.5">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15} className="mt-10">
              <h2 className="font-heading text-2xl font-bold text-brand-navy">{solution.suitableTitle}</h2>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {solution.suitable.map((item) => (
                  <span key={item} className="rounded-full bg-brand-blue/5 border border-brand-blue/15 px-4 py-2 text-sm font-medium text-brand-blue">
                    {item}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2} className="mt-10 overflow-hidden rounded-2xl">
              <img src={solution.image} alt={solution.title} className="w-full object-cover shadow-lg" />
            </Reveal>
          </div>

          <div>
            <Reveal delay={0.1} className="sticky top-32 space-y-5">
              <div className="rounded-2xl bg-brand-navy p-7 text-white sun-rays">
                <h3 className="font-heading text-xl font-bold">Interested in {solution.name}?</h3>
                <p className="mt-2 text-sm text-white/70">Get a free site assessment and a detailed, transparent quotation.</p>
                <button onClick={() => openQuote({ solar_requirement: solution.name })} className="mt-5 w-full rounded-full bg-brand-orange py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orangedark" data-testid="solution-quote-btn">
                  {solution.cta}
                </button>
                <a href={SITE.phone1} className="mt-3 flex items-center justify-center gap-2 rounded-full border border-white/25 py-3.5 font-heading text-sm font-bold text-white transition-colors hover:border-brand-orange hover:text-brand-orange" data-testid="solution-call-btn">
                  <Phone className="h-4 w-4" /> {SITE.phone1Display}
                </a>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-6">
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-brand-navy">Other Solutions</h3>
                <div className="mt-4 space-y-1">
                  {others.map((o) => (
                    <Link key={o.slug} to={`/solutions/${o.slug}`} className="group flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-brand-sky hover:text-brand-blue" data-testid={`solution-related-${o.slug}`}>
                      {o.name}
                      <ArrowRight className="h-3.5 w-3.5 text-brand-orange opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
